import React, { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { NTTData } from "../../assets/brand";
import {
  api,
  formatEventDate,
  isSoldOut,
  passDays,
  rupees,
  setCampaign,
} from "./eventShared";
import "./styles.css"; // kept: other pages may rely on these global rules

const APP_STORE_URL = "https://apps.apple.com/in/app/ota-life/id6743555476";
const PLAY_STORE_URL = "https://play.google.com/store/apps/details?id=com.otanative.ota";

// Public event page (no login needed). The NTT DATA page is the same page with their logo
// and campaign tag (variant="ntt").
const EventPage = ({ variant }) => {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const [event, setEvent] = useState(null);
  const [passes, setPasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async (retried = false) => {
    setLoading(true);
    setError(null);
    try {
      const [productRes, typesRes] = await Promise.all([
        api(`/products/${eventId}`),
        api(`/tickets/ticketTypes/${eventId}`),
      ]);
      setEvent(productRes.data.product);
      setPasses(typesRes.data || []);
    } catch (err) {
      // An expired login is cleared by api(); try once more as a visitor
      if (err.status === 401 && !retried) return await load(true);
      setError(err.status === 404 ? "This event is not available." : err.message);
    } finally {
      setLoading(false);
    }
  }, [eventId]);

  useEffect(() => {
    if (variant === "ntt") setCampaign(eventId, "NTT_DATA_CAMPAIGN", "NTT_DATA");
    load();
  }, [eventId, variant, load]);

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#F5F3FF]">
        <p className="text-gray-500">Loading event...</p>
      </div>
    );
  }

  if (error || !event) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-[#F5F3FF] px-6 text-center">
        <p className="text-lg font-semibold text-gray-800">{error || "Event not found."}</p>
        <div className="flex gap-3">
          <button type="button" onClick={() => load()} className="rounded-xl bg-[#3969E7] px-5 py-2 font-semibold text-white">
            Try again
          </button>
          <Link to="/" className="rounded-xl border border-gray-300 px-5 py-2 font-semibold text-gray-700">
            Home
          </Link>
        </div>
      </div>
    );
  }

  const date = formatEventDate(event.eventDateTime);
  const allSoldOut = passes.length > 0 && passes.every(isSoldOut);

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#F5F3FF] to-white" data-testid="event-page">
      <div className="mx-auto max-w-xl px-4 pb-28 pt-4">
        {variant === "ntt" && (
          <img src={NTTData} alt="Ōta x NTT DATA" className="mx-auto mb-4 max-h-20" />
        )}
        {event.primaryImage && (
          <img src={event.primaryImage} alt={event.title} className="max-h-80 w-full rounded-2xl object-cover shadow" />
        )}

        <h1 className="mt-5 text-2xl font-extrabold text-gray-900">{event.title}</h1>
        <div className="mt-2 space-y-1 text-sm text-gray-600">
          {date && <p>{date}</p>}
          {(event.venue || event.city) && <p>{[event.venue, event.city].filter(Boolean).join(", ")}</p>}
        </div>

        {event.isBooking ? (
          <AppOnly />
        ) : (
          <>
            <h2 className="mt-7 text-lg font-bold text-gray-900">Passes</h2>
            {passes.length === 0 ? (
              <p className="mt-2 text-sm text-gray-500">Passes are not on sale yet.</p>
            ) : (
              <ul className="mt-3 space-y-2" data-testid="event-passes">
                {passes.map((pass) => {
                  const soldOut = isSoldOut(pass);
                  const days = passDays(pass);
                  return (
                    <li
                      key={pass.id}
                      className={`flex items-center justify-between gap-3 rounded-xl border bg-white p-3 ${soldOut ? "opacity-60" : ""}`}
                    >
                      <div className="min-w-0">
                        <p className="font-semibold text-gray-900">{pass.ticketType}</p>
                        {days && <p className="text-xs text-gray-500">{days}</p>}
                      </div>
                      {soldOut ? (
                        <span className="shrink-0 rounded-full bg-gray-200 px-3 py-1 text-xs font-semibold text-gray-600">
                          Sold out
                        </span>
                      ) : (
                        <span className="shrink-0 font-bold text-gray-900">{rupees(pass.totalPrice)}</span>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
            {passes.length > 0 && <p className="mt-2 text-xs text-gray-400">Prices include GST.</p>}
          </>
        )}

        {event.description && (
          <>
            <h2 className="mt-7 text-lg font-bold text-gray-900">About the event</h2>
            <p className="mt-2 whitespace-pre-line text-sm leading-relaxed text-gray-700">{event.description}</p>
          </>
        )}

        <div className="mt-8 flex justify-center gap-4 text-xs text-gray-400">
          <Link to="/terms-and-conditions">Terms &amp; Conditions</Link>
          <a href="/privacy-policy">Privacy Policy</a>
        </div>
      </div>

      {!event.isBooking && passes.length > 0 && (
        <div className="fixed inset-x-0 bottom-0 border-t bg-white/95 p-4 backdrop-blur">
          <button
            type="button"
            disabled={allSoldOut}
            onClick={() => navigate(`/events/checkout?event=${eventId}`)}
            className="mx-auto block w-full max-w-xl rounded-xl bg-gradient-to-r from-[#3969E7] to-[#7D2AE7] py-3 text-base font-bold text-white disabled:from-gray-400 disabled:to-gray-400"
            data-testid="event-buy"
          >
            {allSoldOut ? "Sold out" : "Buy tickets"}
          </button>
        </div>
      )}
    </div>
  );
};

export const AppOnly = () => (
  <div className="mt-7 rounded-xl border bg-white p-4 text-sm text-gray-700">
    <p>Reservations for this event are made in the Ōta LIFE app.</p>
    <div className="mt-3 flex gap-3">
      <a href={APP_STORE_URL} target="_blank" rel="noopener noreferrer" className="flex-1 rounded-xl bg-black px-4 py-2 text-center font-semibold text-white">
        App Store
      </a>
      <a href={PLAY_STORE_URL} target="_blank" rel="noopener noreferrer" className="flex-1 rounded-xl bg-black px-4 py-2 text-center font-semibold text-white">
        Google Play
      </a>
    </div>
  </div>
);

export default EventPage;
